import React, { type FC, type ReactNode } from 'react';
import noop from 'lodash/noop';
import InfoIcon from '@atlaskit/icon/core/status-information';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import { xcss, Inline, Flex } from '@atlaskit/primitives';
import Anchor from '@atlaskit/primitives/anchor';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { FeedbackForm } from '@atlassian/jira-atlassian-intelligence/src/common/ui/feedback-form/index.tsx';
import { useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { AiExperienceFeedbackForm } from '../../types.tsx';
import messages from './messages.tsx';

export type Props = {
	testId?: string;
	feedbackCollectorId: string;
	feedbackFormEntrypointId: string;
	aiExperience: AiExperienceFeedbackForm;
	onAiFeedbackSubmittedEvent: (aiFeedbackResult: 'up' | 'down', payload?: unknown) => void;
	onFeedbackClicked?: (event: React.MouseEvent) => void;
	footerActionNode?: ReactNode;
	footerContainerStyles?: ReturnType<typeof xcss>;
};

export const AiFooter: FC<Props> = ({
	testId,
	feedbackCollectorId,
	feedbackFormEntrypointId,
	onFeedbackClicked = noop,
	onAiFeedbackSubmittedEvent,
	footerActionNode,
	aiExperience,
	footerContainerStyles,
}) => {
	const { formatMessage } = useIntl();
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);

	return (
		<Inline
			testId={testId}
			xcss={[
				fg('jira-ai-suggested-works-ui-update') ? footerStyle : footerStyleOld,
				...(footerContainerStyles ? [footerContainerStyles] : []),
			]}
			spread="space-between"
			alignBlock="center"
		>
			<Flex alignItems="center" gap="space.050">
				<>
					{fg('jira-ai-suggested-works-ui-update') ? (
						<InformationCircleIcon
							label={formatMessage(messages.infoIconLabel)}
							LEGACY_size="small"
							color={token('color.icon.subtle')}
							testId="ai-proactive-container.ui.footer.info-icon"
						/>
					) : (
						<InfoIcon
							label={formatMessage(messages.infoIconLabel)}
							color={token('color.icon.subtle')}
							testId="ai-proactive-container.ui.footer.info-icon"
						/>
					)}
					<Anchor
						xcss={linkStyle}
						href="https://www.atlassian.com/trust/atlassian-intelligence"
						rel="noopener"
						target="_blank"
						testId="ai-proactive-container.ui.footer.disclaimer-text"
					>
						{formatMessage(messages.disclaimerText)}
					</Anchor>
					{fg('jira-ai-suggested-works-ui-update') && (
						<FeedbackForm
							feedbackCollectorId={feedbackCollectorId}
							feedbackFormEntrypointId={feedbackFormEntrypointId}
							aiExperience={aiExperience}
							onFeedbackClicked={onFeedbackClicked}
							onFeedbackSent={onAiFeedbackSubmittedEvent}
							projectKey={projectKey}
						/>
					)}
				</>
			</Flex>
			<Flex alignItems="center" gap="space.100">
				{!fg('jira-ai-suggested-works-ui-update') && (
					<FeedbackForm
						feedbackCollectorId={feedbackCollectorId}
						feedbackFormEntrypointId={feedbackFormEntrypointId}
						aiExperience={aiExperience}
						onFeedbackClicked={onFeedbackClicked}
						onFeedbackSent={onAiFeedbackSubmittedEvent}
						projectKey={projectKey}
					/>
				)}
				{footerActionNode}
			</Flex>
		</Inline>
	);
};

const footerStyleOld = xcss({
	borderTop: `1px solid ${token('color.border')}`,
	paddingLeft: 'space.200',
	paddingRight: 'space.250',
	paddingBlock: 'space.050',
});

const footerStyle = xcss({
	borderTop: `1px solid ${token('color.border')}`,
	paddingLeft: 'space.200',
	paddingRight: 'space.200',
	paddingBlock: 'space.100',
});

const linkStyle = xcss({
	textDecoration: 'none',
	color: 'color.text.subtlest',
	font: token('font.body.small'),
});
